<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import Copilot from 'dashboard/components-next/copilot/Copilot.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useConfig } from 'dashboard/composables/useConfig';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  currentChat: { required: true, type: Object },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const { isEnterprise } = useConfig();

// Tab: 'crm' or 'copilot'
const activeTab = ref('crm');

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closePanel = (force = false) => {
  if (isSmallScreen.value || force) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
    });
  }
};

// ─── Copilot setup (same logic as CopilotContainer) ───
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter('accounts/isFeatureEnabledonAccount');
const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('captainAssistants/getRecords');
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getCopilotAssistant');
const currentChat2 = useMapGetter('getSelectedChat');

const showCopilotTab = computed(() => {
  if (!isEnterprise) return false;
  return isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN);
});

const selectedCopilotThreadId = ref(null);
// Persist thread per conversation so switching back restores context
const STORAGE_KEY = 'airjump:copilot_threads_by_conv';
const loadThreadMap = () => {
  try {
    return new Map(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
  } catch { return new Map(); }
};
const saveThreadMap = map => {
  try {
    // Keep last 50 entries
    const entries = [...map.entries()].slice(-50);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {}
};
const threadsByConversation = loadThreadMap();
const copilotMessages = computed(() =>
  store.getters['copilotMessages/getMessagesByThreadId'](selectedCopilotThreadId.value)
);

// Single shared assistant for all operators — no selector needed
const activeAssistant = computed(() => assistants.value[0]);

const handleCopilotReset = () => {
  // Clear from map too so a new thread starts
  const convId = props.currentChat?.id;
  if (convId) threadsByConversation.delete(convId);
  selectedCopilotThreadId.value = null;
};

// Save/restore copilot thread when conversation changes
watch(() => props.currentChat?.id, (newId, oldId) => {
  // Save current thread for the conversation we're leaving
  if (oldId && selectedCopilotThreadId.value) {
    threadsByConversation.set(oldId, selectedCopilotThreadId.value);
    saveThreadMap(threadsByConversation);
  }
  // Restore thread for the conversation we're entering
  const savedThread = newId ? (threadsByConversation.get(newId) || null) : null;
  selectedCopilotThreadId.value = savedThread;
  // Load messages for the restored thread if not already in store
  if (savedThread && !store.getters['copilotMessages/getMessagesByThreadId'](savedThread).length) {
    store.dispatch('copilotMessages/fetchByThread', savedThread).catch(() => {});
  }
});

const sendCopilotMessage = async message => {
  try {
    if (selectedCopilotThreadId.value) {
      await store.dispatch('copilotMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat2.value?.id,
        threadId: selectedCopilotThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('copilotThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat2.value?.id,
        message,
      });
      selectedCopilotThreadId.value = response.id;
      const convId = currentChat2.value?.id;
      if (convId) {
        threadsByConversation.set(convId, response.id);
        saveThreadMap(threadsByConversation);
      }
    }
  } catch (error) {
    useAlert(error.message);
  }
};

onMounted(() => {
  if (isEnterprise) {
    store.dispatch('captainAssistants/get');
  }
});
</script>

<template>
  <div
    v-on-click-outside="[
      () => closePanel(),
      {
        ignore: [
          'dialog.ProseMirror-prompt-backdrop',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none md:flex"
  >
    <!-- Close button (mobile) -->
    <div v-if="isSmallScreen" class="flex items-center justify-between px-3 py-2 border-b border-n-weak shrink-0">
      <span class="text-sm font-medium text-n-slate-12">Panel</span>
      <button class="p-1 rounded text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2" @click="closePanel()">
        <i class="i-lucide-x text-lg" />
      </button>
    </div>
    <!-- Tab header -->
    <div v-if="showCopilotTab" class="flex border-b border-n-weak shrink-0">
      <button
        class="flex-1 py-2.5 text-sm font-medium text-center transition-colors"
        :class="activeTab === 'crm'
          ? 'text-n-slate-12 border-b-2 border-n-blue-9'
          : 'text-n-slate-9 hover:text-n-slate-11'"
        @click="activeTab = 'crm'"
      >
        CRM
      </button>
      <button
        class="flex-1 py-2.5 text-sm font-medium text-center transition-colors"
        :class="activeTab === 'copilot'
          ? 'text-n-slate-12 border-b-2 border-n-blue-9'
          : 'text-n-slate-9 hover:text-n-slate-11'"
        @click="activeTab = 'copilot'"
      >
        Capitán IA
      </button>
    </div>

    <!-- CRM panel -->
    <div v-show="activeTab === 'crm'" class="flex flex-1 overflow-auto">
      <ContactPanel
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
    </div>

    <!-- Copilot panel -->
    <div v-if="showCopilotTab" v-show="activeTab === 'copilot'" class="flex flex-1 overflow-hidden flex-col">
      <Copilot
        :messages="copilotMessages"
        :support-agent="currentUser"
        :conversation-inbox-type="currentChat.meta?.channel || ''"
        :assistants="assistants.length ? [assistants[0]] : []"
        :active-assistant="activeAssistant"
        @send-message="sendCopilotMessage"
        @reset="handleCopilotReset"
      />
    </div>
  </div>
</template>
